import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Text,
  Spinner,
  VStack,
  HStack,
  Icon,
  Image,
  AspectRatio,
  SimpleGrid,
} from "@chakra-ui/react";
import {
  VscChevronDown,
  VscChevronRight,
  VscRefresh,
  VscSparkle,
  VscTrash,
  VscLock,
  VscPackage,
} from "react-icons/vsc";
import { useAuth, useClerk } from "@clerk/clerk-react";
import { clearAllZustandStorage, usePlacedModelsStore, useModelStore, usePlexianStore } from "../../store";
import ConfirmModal from "../ConfirmModal";
import { assetLoader } from "../../store/apiStore";
import { useLicenseStore } from "../../store/licenseStore";
import { ROUTES } from "../../constants/routes";
import { track, Events } from "../../utils/analytics";

// Component for rendering hierarchical category structure
// Memoized to prevent re-renders when sibling categories change
const HierarchicalCategory = React.memo(function HierarchicalCategory({
  categoryName,
  categoryData,
  expandedCategories,
  onToggleCategory,
  onModelClick,
  loadingAssetId,
  searchTerm = "",
  level = 0,
  isAssetLocked,
}) {
  const hasModels = categoryData.models && categoryData.models.length > 0;
  const hasSubcategories =
    categoryData.subcategories &&
    Object.keys(categoryData.subcategories).length > 0;

  // Filter models by search term
  const filteredModels = hasModels
    ? categoryData.models.filter(
      (asset) =>
        !searchTerm ||
        asset.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : [];

  // Skip rendering if searching and no matches
  if (
    searchTerm &&
    filteredModels.length === 0 &&
    (!hasSubcategories ||
      !Object.values(categoryData.subcategories).some(
        (sub) =>
          sub.models &&
          sub.models.some((asset) =>
            asset.name.toLowerCase().includes(searchTerm.toLowerCase())
          )
      ))
  ) {
    return null;
  }

  const categoryKey = `${level}-${categoryName}`;
  const isExpanded = expandedCategories.has(categoryKey);
  const isTopLevel = level === 0;
  const isSubcategory = level === 1;

  function getAllModels(categoryData, searchTerm = "") {
    const matchesSearch = (asset) =>
      !searchTerm ||
      asset.name.toLowerCase().includes(searchTerm.toLowerCase());

    let models = [];

    // Parent's own models
    if (categoryData.models) {
      models = models.concat(categoryData.models.filter(matchesSearch));
    }

    // Recurse into subcategories
    if (categoryData.subcategories) {
      Object.values(categoryData.subcategories).forEach((sub) => {
        models = models.concat(getAllModels(sub, searchTerm));
      });
    }

    return models;
  }

  // Dynamic layout
  const columns = 4; // Always use 4 columns when expanded
  const thumbSize = "64px";
  const visibleModels =
    hasSubcategories && isExpanded
      ? filteredModels
      : getAllModels(categoryData, searchTerm);

  // Get first model for teaser image (both top-level and subcategories)
  const teaserModel = !isExpanded ? visibleModels[0] : null;

  return (
    <Box key={categoryKey} borderBottom="1px solid" borderColor="gray.700">
      {/* Category Header */}
      <HStack
        _hover={{ bg: "gray.800" }}
        bg={isExpanded ? "gray.700" : "transparent"}
        px={2 + level * 8}
        py={isTopLevel ? 3 : isSubcategory ? 2 : 1} // Taller for top level, medium for subcategories
        cursor="pointer"
        fontSize={isTopLevel ? "lg" : "sm"}
        onClick={() => onToggleCategory(categoryKey, level)}
        spacing={3}
      >
        {/* Teaser thumbnail for collapsed categories (top-level and subcategories) */}
        {!isExpanded && teaserModel && (
          <Box
            w={isTopLevel ? "48px" : "40px"}
            h={isTopLevel ? "48px" : "40px"}
            flexShrink={0}
          >
            <ModelThumbnail
              asset={teaserModel}
              onClick={() => onToggleCategory(categoryKey, level)}
              isLoading={false}
              size={isTopLevel ? "48px" : "40px"}
              isExpanded={false}
            />
          </Box>
        )}

        <Text
          flex="1"
          fontWeight={isTopLevel ? "bold" : "medium"}
          textTransform="capitalize"
          color={isTopLevel ? "white" : "gray.200"}
        >
          {categoryName}
        </Text>
        <Icon
          as={isExpanded ? VscChevronDown : VscChevronRight}
          fontSize={isTopLevel ? "md" : "xs"}
          color="gray.400"
        />
      </HStack>

      {/* Models grid - only show when expanded */}
      {isExpanded && visibleModels.length > 0 && (
        <SimpleGrid columns={columns} spacing={2} px={2 + level * 8} py={2}>
          {visibleModels.map((asset) => {
            const locked = isAssetLocked ? isAssetLocked(asset) : false;
            return (
              <Box key={asset.id} w={thumbSize} h={thumbSize}>
                <ModelThumbnail
                  asset={asset}
                  onClick={() => onModelClick(asset, locked)}
                  isLoading={loadingAssetId === asset.id}
                  size={thumbSize}
                  isExpanded={isExpanded}
                  isLocked={locked}
                />
              </Box>
            );
          })}
        </SimpleGrid>
      )}

      {/* Subcategories */}
      {hasSubcategories &&
        isExpanded &&
        Object.entries(categoryData.subcategories).map(([subName, subData]) => (
          <HierarchicalCategory
            key={subName}
            categoryName={subName}
            categoryData={subData}
            expandedCategories={expandedCategories}
            onToggleCategory={onToggleCategory}
            onModelClick={onModelClick}
            loadingAssetId={loadingAssetId}
            searchTerm={searchTerm}
            level={level + 1}
            isAssetLocked={isAssetLocked}
          />
        ))}
    </Box>
  );
});

// Component to handle model thumbnail display with proper loading
// Memoized to prevent re-renders when other thumbnails load
const ModelThumbnail = React.memo(function ModelThumbnail({ asset, onClick, isLoading, isExpanded, isLocked }) {
  const [thumbnailError, setThumbnailError] = useState(false);

  // Use thumbnailUrl directly from the backend response
  const thumbnailSrc = asset?.thumbnailUrl;

  const handleImageError = (e) => {
    console.log(
      "🖼️ Thumbnail load failed for",
      asset?.name,
      "- URL was:",
      e.target.src
    );
    if (e.target.src !== "https://placehold.co/60x60/orange/white") {
      setThumbnailError(true);
      e.target.src = "https://placehold.co/60x60/orange/white";
    }
  };

  const handleImageLoad = () => {
    console.log("✅ Thumbnail loaded successfully for", asset?.name);
  };

  return (
    <Box
      bg="gray.800"
      borderRadius="md"
      _hover={{ bg: isLocked ? "gray.800" : "gray.700" }}
      cursor={isLocked ? "not-allowed" : "pointer"}
      onClick={onClick}
      position="relative"
      textAlign="center"
    >
      <AspectRatio ratio={1} w="100%">
        <Image
          src={
            thumbnailError
              ? "https://placehold.co/60x60/orange/white"
              : thumbnailSrc || "https://placehold.co/60x60/gray/white"
          }
          alt={asset?.name || "Model thumbnail"}
          objectFit="cover"
          borderRadius={isExpanded ? "xl" : "5px"}
          border={"solid transparent 3px"}
          onError={handleImageError}
          onLoad={handleImageLoad}
          crossOrigin="anonymous"
          opacity={isLocked ? 0.5 : 1}
        />
      </AspectRatio>

      {/* Lock overlay */}
      {isLocked && (
        <Box
          position="absolute"
          inset={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="blackAlpha.500"
          borderRadius={isExpanded ? "xl" : "5px"}
        >
          <Icon as={VscLock} color="white" boxSize={4} />
        </Box>
      )}

      {/* Model name tooltip on hover */}
      {isExpanded && !isLocked && (
        <Text
          position="absolute"
          bottom="0"
          left="0"
          right="0"
          bg="blackAlpha.700"
          color="white"
          fontSize="xs"
          p="1"
          borderBottomRadius="xl"
          opacity="0"
          _hover={{ opacity: "1" }}
          transition="opacity 0.2s"
          noOfLines={1}
        >
          {asset?.name}
        </Text>
      )}
      {isLoading && (
        <Spinner
          size="xs"
          position="absolute"
          right={2}
          top={2}
          color="blue.400"
        />
      )}
    </Box>
  );
});

function PlexianSection() {
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useAuth();
  const { openSignIn } = useClerk();
  const avatarConfig = usePlexianStore((s) => s.avatarConfig);
  const avatarId = usePlexianStore((s) => s.avatarId);
  const addModel = usePlacedModelsStore((s) => s.addModel);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleMakeAvatar = () => {
    const returnTo = window.location.pathname;
    navigate(`/avatar/create?returnTo=${encodeURIComponent(returnTo)}`);
  };

  const handleSignIn = () => {
    openSignIn({ redirectUrl: window.location.pathname });
  };

  if (!isLoaded) return null;

  const renderHeader = () => (
    <HStack
      _hover={{ bg: "gray.800" }}
      bg={isExpanded ? "gray.700" : "transparent"}
      px={2}
      py={3}
      cursor="pointer"
      onClick={() => setIsExpanded((prev) => !prev)}
      spacing={3}
    >
      {!isExpanded && (
        <Box w="48px" h="48px" flexShrink={0} display="flex" alignItems="center" justifyContent="center">
          <Box w="42px" h="42px" bg="gray.800" borderRadius="md">
            <AspectRatio ratio={1} w="100%">
              <Image
                src="/mogulplex-favicon-32x32newx.png"
                alt="My Plexian"
                objectFit="contain"
                borderRadius="5px"
                border="solid transparent 3px"
                bg="gray.300"
              />
            </AspectRatio>
          </Box>
        </Box>
      )}
      <Text flex="1" fontSize="lg" fontWeight="bold" color="white" textTransform="capitalize">
        My Plexian
      </Text>
      <Icon
        as={isExpanded ? VscChevronDown : VscChevronRight}
        fontSize="md"
        color="gray.400"
      />
    </HStack>
  );

  if (!isSignedIn) {
    return (
      <Box borderBottom="1px solid" borderColor="gray.700">
        {renderHeader()}
        {isExpanded && (
          <VStack px={2} py={3} spacing={2} align="stretch">
            <Text fontSize="xs" color="gray.400" px={1}>
              Sign in to create and place your Plexian avatar.
            </Text>
            <Button
              size="sm"
              w="full"
              bg="purple.800"
              color="purple.200"
              borderWidth="1px"
              borderColor="purple.600"
              _hover={{ bg: "purple.700", color: "white" }}
              fontWeight="semibold"
              onClick={handleSignIn}
              leftIcon={<Icon as={VscSparkle} />}
            >
              Sign in
            </Button>
          </VStack>
        )}
      </Box>
    );
  }

  const handlePlace = () => {
    // If the avatar is set up for interaction, give the placement a hoverText
    // so the standard CenterRaycaster picks it up (outline + "TALK TO PLEXIAN").
    const interactionEnabled = !!avatarConfig?.interactionEnabled
      && (avatarConfig?.interactiveText || '').length > 0;
    const newId = addModel({
      name: "My Plexian",
      url: null,
      type: "plexian_avatar",
      avatarConfig,
      avatarId: avatarId || null,
      hoverText: interactionEnabled ? "Talk to Plexian" : "",
      position: [Math.random() * 60 - 30, 0, Math.random() * 60 - 30],
      scale: [8, 8, 8],
      rotation: [0, 0, 0],
    });
    if (newId) {
      useModelStore.getState().setPendingSelectionId(newId);
    }
  };

  return (
    <Box borderBottom="1px solid" borderColor="gray.700">
      {renderHeader()}
      {isExpanded && (
        <VStack px={2} py={3} spacing={2} align="stretch">
          {avatarConfig ? (
            <Button
              size="sm"
              w="full"
              bg="purple.700"
              color="white"
              _hover={{ bg: "purple.600" }}
              fontWeight="semibold"
              onClick={handlePlace}
              leftIcon={<Icon as={VscSparkle} />}
            >
              Place in Room
            </Button>
          ) : (
            <Button
              size="sm"
              w="full"
              bg="purple.800"
              color="purple.200"
              borderWidth="1px"
              borderColor="purple.600"
              _hover={{ bg: "purple.700", color: "white" }}
              fontWeight="semibold"
              onClick={handleMakeAvatar}
              leftIcon={<Icon as={VscSparkle} />}
            >
              Make Avatar
            </Button>
          )}
          {avatarConfig && (
            <Button
              size="xs"
              variant="ghost"
              color="gray.500"
              onClick={handleMakeAvatar}
              alignSelf="center"
            >
              Edit avatar
            </Button>
          )}
        </VStack>
      )}
    </Box>
  );
}

function ModelList() {
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingAssetId, setLoadingAssetId] = useState(null);
  const [assets, setAssets] = useState([]);
  const [hierarchicalCategories, setHierarchicalCategories] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const addURL = usePlacedModelsStore((s) => s.addModel);

  const hasLicense = useLicenseStore((s) => s.hasLicense);
  const isShopItem = useLicenseStore((s) => s.isShopItem);

  /** Returns true if this asset is in the shop and the user doesn't own a license */
  const isAssetLocked = (asset) => {
    const id = asset?.id;
    if (!id) return false;
    return isShopItem(id) && !hasLicense(id);
  };

  // Load assets on component mount using assetLoader with caching
  // Note: AssetLoader is initialized in EditorPage to avoid redundant initialization
  useEffect(() => {
    const loadAssets = async () => {
      try {
        setIsLoading(true);
        setError(null);
        console.log("🔄 Loading assets from assetLoader with caching...");

        // First try to get hierarchical structure
        const hierarchical = await assetLoader.getHierarchicalCategories();

        if (hierarchical && Object.keys(hierarchical).length > 0) {
          console.log(
            `📊 Using hierarchical categories with ${
              Object.keys(hierarchical).length
            } top-level categories`
          );
          setHierarchicalCategories(hierarchical);

          // Extract all models from hierarchical structure for search
          const allModels = [];
          Object.values(hierarchical).forEach((category) => {
            // Add models from top-level category
            if (category.models && Array.isArray(category.models)) {
              allModels.push(...category.models);
            }
            // Add models from subcategories
            if (
              category.subcategories &&
              typeof category.subcategories === "object"
            ) {
              Object.values(category.subcategories).forEach((subcategory) => {
                if (subcategory.models && Array.isArray(subcategory.models)) {
                  allModels.push(...subcategory.models);
                }
              });
            }
          });
          setAssets(allModels);
          console.log(
            `✅ Extracted ${allModels.length} total models from hierarchical structure`
          );
        } else {
          // Fallback to flat structure
          console.log(
            "🔄 Hierarchical categories not available, using flat structure..."
          );
          const models = await assetLoader.getAvailableModels();
          setAssets(Array.isArray(models) ? models : []);
          setHierarchicalCategories(null);
          console.log(
            `✅ Loaded ${models?.length || 0} assets from flat structure`
          );
        }
      } catch (error) {
        console.error("❌ Failed to load assets from assetLoader:", error);
        setAssets([]);
        setHierarchicalCategories(null);
        setError(`Failed to load assets: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    loadAssets();
  }, []); // Keep empty dependency array since assetLoader handles caching

  // Group assets by category
  const assetsByCategory = React.useMemo(() => {
    const filtered = assets.filter(
      (asset) =>
        !searchTerm ||
        asset.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const grouped = {};
    filtered.forEach((asset) => {
      const category = asset.category || "other";
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(asset);
    });

    return grouped;
  }, [assets, searchTerm]);

  const toggleCategory = (category, isSubCategory = false) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(isSubCategory ? prev : undefined);

      if (prev.has(category) && !isSubCategory) {
        // If clicking a top-level category that's already open, close everything
        return newSet;
      } else if (newSet.has(category)) {
        // If clicking an already-open subcategory, close it
        newSet.delete(category);
      } else {
        // Opening a new category
        newSet.add(category);

        // If opening a subcategory (level 1), close other subcategories at the same level
        if (isSubCategory) {
          const level = category.split('-')[0]; // Get the level number
          // Remove all other categories at the same level
          Array.from(newSet).forEach(cat => {
            if (cat.startsWith(`${level}-`) && cat !== category) {
              newSet.delete(cat);
            }
          });
        }
      }

      return newSet;
    });
  };

  // Auto-expand all categories when searching
  // Only depends on searchTerm to avoid re-running when hierarchicalCategories updates
  useEffect(() => {
    if (searchTerm.trim()) {
      // When searching, expand all categories to show results
      const categoriesToExpand = new Set();

      if (hierarchicalCategories) {
        // Add all hierarchical categories
        Object.keys(hierarchicalCategories).forEach((categoryName) => {
          categoriesToExpand.add(`0-${categoryName}`);

          // Add subcategories
          const category = hierarchicalCategories[categoryName];
          if (category.subcategories) {
            Object.keys(category.subcategories).forEach((subCategoryName) => {
              categoriesToExpand.add(`1-${subCategoryName}`);
            });
          }
        });
      } else {
        // Flat structure
        Object.keys(assetsByCategory).forEach((categoryName) => {
          categoriesToExpand.add(categoryName);
        });
      }

      setExpandedCategories(categoriesToExpand);
    } else {
      // Clear expanded categories when not searching
      setExpandedCategories(new Set());
    }
    // CRITICAL: Only depend on searchTerm, NOT on hierarchicalCategories
    // hierarchicalCategories is recreated on every load, which would cause infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Handle adding a model to the scene
  const handleAddModel = async (asset, locked = false) => {
    if (locked) {
      navigate('/collection');
      return;
    }
    try {
      setLoadingAssetId(asset.id);

      // Log asset properties to debug scale issues
      console.log('🔍 Asset properties:', asset);

      // Get presigned URL for the model using assetLoader
      const modelUrl = await assetLoader.getModelUrl(asset);

      if (!modelUrl) {
        throw new Error("No URL available for model");
      }

      // Use asset's default scale if available, otherwise default to [1, 1, 1]
      const modelScale = asset.defaultScale || asset.scale || [1, 1, 1];
      console.log('📏 Using scale:', modelScale, 'for model:', asset.name);

      // Track first asset placement per room
      const currentModels = usePlacedModelsStore.getState().placedModels;
      const isFirstAsset = currentModels.length === 0;

      // Add model to the scene and queue it for auto-selection
      const newId = addURL({
        url: modelUrl,
        name: asset.name,
        assetId: asset.id,
        category: asset.category,
        position: [Math.random() * 100 - 50, 0, Math.random() * 100 - 50],
        scale: modelScale,
      });

      if (newId) {
        useModelStore.getState().setPendingSelectionId(newId);

        if (isFirstAsset) {
          const roomId = usePlacedModelsStore.getState()._roomId;
          track(Events.ASSET_PLACED, {
            roomId,
            assetName: asset.name,
            assetCategory: asset.category,
          });
        }
      }
    } catch (error) {
      console.error("Failed to add model:", error);
    } finally {
      setLoadingAssetId(null);
    }
  };

  // Handle refreshing assets - force refresh by clearing cache first
  const handleRefresh = async () => {
    try {
      setIsLoading(true);
      setError(null);
      console.log(
        "🔄 Refreshing assets - clearing cache and fetching fresh data..."
      );

      // Clear cache to force fresh data fetch
      assetLoader.clearCache();

      // Re-initialize which will fetch fresh data
      const initialized = await assetLoader.initialize(true); // Force refresh

      if (!initialized) {
        setError(
          "Failed to connect to asset service. Please check your connection."
        );
        return;
      }

      // Try to get hierarchical structure first
      const hierarchical = await assetLoader.getHierarchicalCategories();

      if (hierarchical && Object.keys(hierarchical).length > 0) {
        console.log(
          `📊 Refreshed with hierarchical categories: ${
            Object.keys(hierarchical).length
          } top-level categories`
        );
        setHierarchicalCategories(hierarchical);

        // Extract all models from hierarchical structure
        const allModels = [];
        Object.values(hierarchical).forEach((category) => {
          if (category.models && Array.isArray(category.models))
            allModels.push(...category.models);
          if (category.subcategories) {
            Object.values(category.subcategories).forEach((subcategory) => {
              if (subcategory.models && Array.isArray(subcategory.models))
                allModels.push(...subcategory.models);
            });
          }
        });
        setAssets(allModels);
        console.log(
          `✅ Refreshed ${allModels.length} total models from hierarchical structure`
        );
      } else {
        // Fallback to flat structure
        const models = await assetLoader.getAvailableModels();
        setHierarchicalCategories(null);
        setAssets(Array.isArray(models) ? models : []);
        console.log(
          `✅ Refreshed ${models?.length || 0} assets from flat structure`
        );
      }
    } catch (error) {
      console.error("❌ Failed to refresh assets:", error);
      setError(`Refresh failed: ${error.message}`);
      // Don't clear existing assets on refresh failure
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <VStack spacing={3} p={3}>
        <Spinner size="md" />
        <Text fontSize="sm" color="gray.400">
          Loading assets...
        </Text>
      </VStack>
    );
  }

  if (error) {
    return (
      <VStack spacing={3} p={3}>
        <Text fontSize="sm" color="red.400" textAlign="center">
          ⚠️ {error}
        </Text>
        <Button
          size="sm"
          variant="outline"
          colorPalette="blue"
          onClick={handleRefresh}
          w="full"
        >
          <Icon as={VscRefresh} />
          Try Again
        </Button>
      </VStack>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      {/* Shop CTA — links to /collection */}
      <Box px={2}>
        <Button
          size="sm"
          w="full"
          variant="outline"
          color="gray.300"
          borderColor="gray.600"
          bg="transparent"
          _hover={{ bg: "gray.800", color: "white", borderColor: "gray.500" }}
          onClick={() => navigate(ROUTES.COLLECTION)}
          leftIcon={<Icon as={VscPackage} />}
        >
          Get More Assets
        </Button>
      </Box>

      {/* My Plexian — sits alongside furniture, decorations, etc. */}
      <PlexianSection />

      {/* Search Control */}
      {/* <Input
        placeholder="Search assets..."
        size="sm"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        bg="gray.800"
        borderColor="gray.600"
        _hover={{ borderColor: "gray.500" }}
        _focus={{ borderColor: "blue.400" }}
      />
      <Text fontSize="xs" color="gray.400" textAlign="center">
        {assets.length} assets available
      </Text> */}

      {/* Hierarchical Categories or Flat Categories */}
      <VStack spacing={1} align="stretch">
        {hierarchicalCategories
          ? Object.entries(hierarchicalCategories)
            .sort(([a], [b]) => {
              // Define custom order for top-level categories
              const categoryOrder = [
                'furniture',
                'decorations',
                'music',
                'art',
                'floor',
                'electronics',
                'characters',
                'food',
                'office',
                'sports'
              ];

              const aLower = a.toLowerCase();
              const bLower = b.toLowerCase();
              const aIndex = categoryOrder.indexOf(aLower);
              const bIndex = categoryOrder.indexOf(bLower);

              // If both are in the order list, sort by their position
              if (aIndex !== -1 && bIndex !== -1) {
                return aIndex - bIndex;
              }
              // If only a is in the list, it comes first
              if (aIndex !== -1) return -1;
              // If only b is in the list, it comes first
              if (bIndex !== -1) return 1;
              // If neither is in the list, sort alphabetically
              return aLower.localeCompare(bLower);
            })
            .map(([categoryName, categoryData]) => (
              <HierarchicalCategory
                key={categoryName}
                categoryName={categoryName}
                categoryData={categoryData}
                expandedCategories={expandedCategories}
                onToggleCategory={toggleCategory}
                onModelClick={handleAddModel}
                loadingAssetId={loadingAssetId}
                searchTerm={searchTerm}
                level={0}
                isAssetLocked={isAssetLocked}
              />
            ))
          : Object.entries(assetsByCategory).map(
              ([category, categoryAssets]) => {
                const isExpanded = expandedCategories.has(category);
                const columns = isExpanded ? 4 : 8;
                const thumbSize = isExpanded ? "64px" : "32px";
                const visibleAssets = categoryAssets;

                return (
                  <Box
                    key={category}
                    borderBottom="1px solid"
                    borderColor="gray.700"
                  >
                    <HStack
                      _hover={{ bg: "gray.800" }}
                      bg={isExpanded ? "gray.700" : "transparent"}
                      px={2}
                      py={2}
                      cursor="pointer"
                      fontSize="sm"
                      onClick={() => toggleCategory(category)}
                    >
                      <Text
                        flex="1"
                        fontWeight="medium"
                        textTransform="capitalize"
                      >
                        {category}
                      </Text>
                      <Icon
                        as={isExpanded ? VscChevronDown : VscChevronRight}
                        fontSize="xs"
                        color="gray.400"
                      />
                    </HStack>

                    <SimpleGrid columns={columns} spacing={2}>
                      {visibleAssets.map((asset) => {
                        const locked = isAssetLocked(asset);
                        return (
                        <Box key={asset.id} w={thumbSize} h={thumbSize}>
                          <ModelThumbnail
                            asset={asset}
                            onClick={
                              isExpanded
                                ? () => handleAddModel(asset, locked)
                                : () => toggleCategory(category)
                            }
                            isLoading={loadingAssetId === asset.id}
                            size={thumbSize}
                            isExpanded={isExpanded}
                            isLocked={locked}
                          />
                        </Box>
                        );
                      })}
                    </SimpleGrid>
                  </Box>
                );
              }
            )}
      </VStack>

      {/* Footer Controls */}
      <VStack spacing={2} pt={2} borderTop="1px solid" borderColor="gray.600">
        {/* <Button
          size="xs"
          variant="outline"
          colorPalette="blue"
          color="white"
          onClick={handleRefresh}
          isLoading={isLoading}
          w="full"
        >
          <Icon as={VscRefresh} />
          Refresh Assets
        </Button> */}

        <Button
          size="xs"
          variant="outline"
          colorPalette="red"
          color="white"
          onClick={() => setShowClearConfirm(true)}
          w="full"
        >
          <Icon as={VscTrash} />
          Reset Room
        </Button>
      </VStack>

      {showClearConfirm && (
        <ConfirmModal
          message="Are you sure you want to delete everything in this room?"
          danger
          confirmText="Yes, delete everything"
          onConfirm={() => {
            clearAllZustandStorage();
            setShowClearConfirm(false);
          }}
          onCancel={() => setShowClearConfirm(false)}
        />
      )}
    </VStack>
  );
}

export default ModelList;
